import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';
import { useSuggestedUsers } from '../../hooks/profileQueries';

// 1. Move helper functions outside the component
const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const getProfilePicture = (profilePic) => {
  if (profilePic && typeof profilePic === "object") {
    return profilePic.thumbnail || profilePic.small || profilePic.medium;
  }
  return typeof profilePic === "string" ? profilePic : null;
};

// 2. Move SuggestedUser OUTSIDE the main component
const SuggestedUser = ({ user, onNavigate }) => {
  const profilePictureUrl = getProfilePicture(user.profileImage);

  return (
    <div className="flex items-center justify-between py-2 px-1">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-gray-100">
          {profilePictureUrl ? (
            <img src={profilePictureUrl} alt={user.username} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
               <span className="text-slate-600 font-semibold text-xs">{getInitials(user?.name)}</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h3 className="font-semibold text-sm text-gray-900 truncate">{user.username}</h3>
            {user.isVerified && <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />}
          </div>
        </div>
      </div>

      <button
        type="button" // Important: prevents form submission behavior
        onClick={() => onNavigate(user.username)}
        className="text-xs text-indigo-600 font-bold hover:text-[#b0d284] transition-colors ml-2"
      >
        View Profile
      </button>
    </div>
  );
};

const RightSidebar = ({ isVisible = true }) => {
  // Pass isVisible as the 'enabled' property inside the options object
  const { data: suggestedUsers = [], isLoading } = useSuggestedUsers({ 
    enabled: isVisible 
  });
  
  const navigate = useNavigate();

  const handleNavigate = (username) => {
    if (!username) return;
    navigate(`/${username}`);
  };

  // Double safety: If not visible, return null
  if (!isVisible) return null;

  return (
    <aside className="hidden xl:block w-64 xl:w-72 2xl:w-80 sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="px-4">
        <h2 className="text-sm font-semibold text-gray-500 mb-4">Suggested for you</h2>
        {isLoading ? (
          <div className="flex justify-center py-4">
             <span className="text-xs text-gray-400">Loading suggestions...</span>
          </div>
        ) : (
          <div className="space-y-1">
            {suggestedUsers.map((user) => (
              <SuggestedUser 
                key={user.id || user.username} 
                user={user} 
                onNavigate={handleNavigate} 
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default RightSidebar;